import { useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Divider,
    Button,
    useDisclosure,
    useToast,
    Heading,
    Box,
    Flex,
    Progress
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import { UserManager } from "oidc-client-ts";
import Cookies from 'js-cookie';

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import SelectRegisterType from "@/components/Selects/SelectRegisterType";
import { appConfig } from "@/common/config";
import Footer from "@/components/Layouts/Footer";
import { registerSchema } from "@/schemas/registerSchema";
import { registerInspectionUsers } from "@/services/inspectionUserService";

import './styles/Form.css';

const initialValues = {
    type: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
};

const placeholders: Record<string, string> = {
    firstName: 'Enter First Name',
    lastName: 'Enter Last Name',
    email: 'Enter Email Address',
    password: 'Enter Password',
    confirmPassword: 'Re-enter Password',
};

const formatFieldName = (fieldName: string) => {
    const formattedNames: Record<string, string> = {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password'
    };
    return formattedNames[fieldName] || capitalizeFirstLetter(fieldName);
};

const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

const Register: React.FC = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const userManager = new UserManager(appConfig.settings);

    const [confirmation, setConfirmation] = useState({ text: "", title: "", buttonLabel: "" });
    const [dataToSubmit, setDataToSubmit] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [strength, setStrength] = useState('');

    const showToast = (title: string, description: string, status: 'success' | 'error') => {
        toast({
            title,
            description,
            status,
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleCancel = () => {
        setConfirmation({
            text: "Are you sure you want to cancel?",
            title: "Cancel to Register User?",
            buttonLabel: "Yes"
        });
        onOpen();
    };

    const handleSubmit = (data: any) => {
        setDataToSubmit(data);
        setConfirmation({
            text: "Are you sure you want to register?",
            title: "Register User?",
            buttonLabel: "Register User"
        });
        onOpen();
    };

    const generateDataToSubmit = () => ({
        role: dataToSubmit.type ?? '',
        email: dataToSubmit.email ?? '',
        firstName: dataToSubmit.firstName ?? '',
        lastName: dataToSubmit.lastName ?? '',
        password: dataToSubmit.password ?? '',
        clientId: "react-client-inspection",
    });

    const backToLogin = async () => {
        await userManager.clearStaleState();
        await userManager.signoutRedirect();
    }

    const handleConfirm = async () => {
        onClose();
        setIsSubmitting(true);

        if (confirmation.title === "Cancel to Register User?") {
            backToLogin();
            return;
        }

        const obj = generateDataToSubmit();
        const response = await registerInspectionUsers(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            showToast('User registered.', "We've registered the user.", 'success');
            setTimeout(() => backToLogin(), 3000);
        } else {
            showToast('User failed to register.', response.message || "We were unable to register the user.", 'error');
        }
    };

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    };

    const handleChange = (props: any, fieldName: string, value: string) => {
        props.setFieldValue(fieldName, value);
        if (fieldName === 'password') {
            checkPasswordStrength(value);
        }
    };

    const checkPasswordStrength = (password: string) => {
        let score = 0;

        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        switch (score) {
            case 0:
                setStrength('');
                break;
            case 1:
            case 2:
                setStrength('Weak');
                break;
            case 3:
                setStrength('Medium');
                break;
            case 4:
                setStrength('Strong');
                break;
            default:
                setStrength('Very Strong');
        }
    };

    const getMyStrengthScore = () => {
        switch (strength) {
            case 'Weak':
                return 25;
            case 'Medium':
                return 50;
            case 'Strong':
                return 75;
            case 'Very Strong':
                return 100;
            default:
                return 0;
        }
    }

    const getMyStrengthColor = () => {
        switch (strength) {
            case 'Weak':
                return 'red';
            case 'Medium':
                return '#3182ce';
            case 'Strong':
                return 'green';
            case 'Very Strong':
                return 'green';
            default:
                return 'transparent';
        }
    }

    useEffect(() => {
        Cookies.set('expired_time', 'register');
    }, [])

    return (
        <Flex width="100%" flexDirection="column">
            <Box display="flex" flexDirection="column" bgColor="#f8fafc" width="100%" minHeight="calc(100vh - 70px)" overflowY="auto" alignItems="center">
                <Heading mb={0} mt={10}>Register User</Heading>
                <div className="form-container" style={{ width: 'calc(100% - 20px)' }}>
                    <ModalConfirmation
                        isOpen={isOpen}
                        onClose={onClose}
                        title={confirmation.title}
                        handleCancel={handleConfirmToCancel}
                        handleConfirm={handleConfirm}
                        confirmationText={confirmation.text}
                        confirmButtonText={confirmation.buttonLabel}
                    />
                            <Formik
                                initialValues={initialValues}
                                onSubmit={(values: any) => handleSubmit(values)}
                                validationSchema={registerSchema}
                            >
                                {(props) => (
                                    <Form>
                                        <Field name='type'>
                                            {({ form }: any) => (
                                                <FormControl isInvalid={form.errors.type && form.touched.type} mb={4}>
                                                    <div className="form-field-container">
                                                        <FormLabel htmlFor="type" className="form-label">
                                                            Type<span style={{ color: 'red' }}>*</span>
                                                        </FormLabel>
                                                        <div className="form-input-container">
                                                            <SelectRegisterType
                                                                name='type'
                                                                placeholder="Choose User Type"
                                                                handleChange={({ value }: { value: string }) => form.setFieldValue('type', value)}
                                                                value={form.values.type}
                                                                hasError={form.errors.type && form.touched.type}
                                                            />
                                                            <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                                        </div>
                                                    </div>
                                                </FormControl>
                                            )}
                                        </Field>

                                        {['email', 'firstName', 'lastName', 'password', 'confirmPassword'].map((fieldName) => (
                                            <Field key={fieldName} name={fieldName}>
                                                {({ field, form }: any) => (
                                                    <FormControl isInvalid={form.errors[fieldName] && form.touched[fieldName]} mb={4}>
                                                        <div className="form-field-container">
                                                            <FormLabel htmlFor={fieldName} className="form-label">
                                                                {formatFieldName(fieldName)}<span style={{ color: 'red' }}>*</span>
                                                            </FormLabel>
                                                            <div className="form-input-container">
                                                                <Input
                                                                    {...field}
                                                                    id={fieldName}
                                                                    type={fieldName.toLowerCase().includes('password') ? 'password' : 'text'}
                                                                    placeholder={placeholders[fieldName]}
                                                                    onChange={(e) => handleChange(props, fieldName, e.target.value)}
                                                                />
                                                                {(fieldName === 'password' && getMyStrengthScore() > 0) &&
                                                                    <>
                                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <Box width='100%' borderRadius='10px' marginTop={4}>
                                                                                <Progress
                                                                                    value={getMyStrengthScore()}
                                                                                    sx={{
                                                                                        backgroundColor: '#e0e0e0',
                                                                                        '& > div': {
                                                                                            backgroundColor: getMyStrengthColor(),
                                                                                        },
                                                                                    }}
                                                                                    borderRadius='10px'
                                                                                />
                                                                            </Box>
                                                                        </div>
                                                                        <div style={{ display: 'flex' }}>
                                                                            <span style={{ color: getMyStrengthColor(), width: '100%', textAlign: 'left', fontSize: '0.875rem' }}>{strength}</span>
                                                                        </div>
                                                                    </>
                                                                }
                                                                <FormErrorMessage>{form.errors[fieldName]}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                        
                                                    </FormControl>
                                                )}
                                            </Field>
                                        ))}

                                        <Divider />
                                        <Flex justifyContent="flex-end" mt={4}>
                                            <Button
                                                variant='outline'
                                                onClick={handleCancel}
                                                mr={4}
                                                isLoading={isSubmitting}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                backgroundColor={COLORS.BUTTON.BACKGROUND}
                                                color={COLORS.BUTTON.FOREGROUND}
                                                isLoading={isSubmitting}
                                                type='submit'
                                                rightIcon={<ArrowIcon />}
                                            >
                                                Register
                                            </Button>
                                        </Flex>
                                    </Form>
                                )}
                            </Formik>
                </div>
                
            </Box>
            <Footer />
        </Flex>
    );
};

export default Register;
